import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryOptions } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { makeIsEnabled } from '@cast/utils';
import { ListAKSNodeClassesResponse } from '@cast/types-kent';

const KENT_AKS_NODE_CLASSES_QUERY_KEY = 'kent-aks-node-classes';

export const getAKSNodeClassesQueryKey = (clusterId?: string) =>
  [KENT_AKS_NODE_CLASSES_QUERY_KEY, clusterId] as const;

/** Reusable query options for the AKS node-classes list (also subscribed by the edit dialog's 409 reload). */
export const getAKSNodeClassesQuery = ({
  organizationId,
  clusterId,
}: {
  organizationId: string;
  clusterId: string;
}) =>
  queryOptions({
    queryKey: getAKSNodeClassesQueryKey(clusterId),
    queryFn: async () => {
      const { data } = await apiClient.kent.getAksNodeClasses({
        organizationId,
        clusterId,
      });
      return data;
    },
  });

type Params = {
  clusterId: string;
};

type GetAKSNodeClassesQueryArgs = {
  params: Params;
  options?: QueryOptions<ListAKSNodeClassesResponse>;
};

export const useGetAKSNodeClassesQuery = ({
  params,
  options,
}: GetAKSNodeClassesQueryArgs) => {
  const { currentOrganization } = useOrganizations();
  const organizationId = currentOrganization?.id;

  return useQuery({
    queryKey: getAKSNodeClassesQueryKey(params.clusterId),
    queryFn: async () => {
      const { data } = await apiClient.kent.getAksNodeClasses({
        organizationId: organizationId!,
        clusterId: params.clusterId,
      });
      return data;
    },
    ...options,
    enabled: makeIsEnabled(
      !!organizationId && !!params.clusterId,
      options?.enabled
    ),
  });
};
